import { type HTMLAttributes, useState, useEffect } from 'react';
import { __ } from '@wordpress/i18n';
import { getSettings } from '@wordpress/date';
import { DayPicker } from 'react-day-picker';
import type {
	Mode,
	CalendarMonth,
	DateRange,
	DayEventHandler,
	DayPickerProps,
	Locale,
} from 'react-day-picker';
import { format, addDays } from 'date-fns';
import { formatFullDate } from '../utils/date-time';
import { getDayPickerLocale, getDefaultLocale } from '../utils/locale';
import { useMonthAvailabilityQuery } from '../queries';
import { useBookingStore } from '../store/useBookingStore';
import {
	useService,
	useIsDateRange,
	useSelectedDate,
} from '../hooks/useBookingComputed';
import { useStartingMonth } from '../hooks/useStartingMonth';
import { CalendarSkeleton } from './CalendarSkeleton';
import classnames from 'classnames';
import './DateRangePicker.css';

export function DateRangePicker() {
	const { data: service } = useService();
	const isDateRange = useIsDateRange();

	// Client state from store
	const selectedDate = useSelectedDate();
	const selectedRange = useBookingStore((state) => state.selectedRange);
	const setSelectedDate = useBookingStore((state) => state.setSelectedDate);
	const setSelectedRange = useBookingStore((state) => state.setSelectedRange);
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	const selectedResourceIds = useBookingStore(
		(state) => state.selectedResourceIds
	);
	const resourceMode = useBookingStore((state) => state.resourceMode);
	const excludeBookingId = useBookingStore((state) => state.excludeBookingId);

	// Starting month logic (reads from store)
	const {
		currentMonth,
		onMonthChange,
		loading: startingMonthLoading,
	} = useStartingMonth();

	// Get today at midnight - respects gp_bookings_current_date filter for testing
	const today = (() => {
		const todayDate = window.gform?.applyFilters
			? window.gform.applyFilters('gp_bookings_current_date', new Date())
			: new Date();

		const result = new Date(todayDate);
		result.setHours(0, 0, 0, 0);
		return result;
	})();

	// Fetch month availability
	const { data: monthAvailabilityData, isLoading: monthLoading } =
		useMonthAvailabilityQuery(
			{
				serviceId: currentServiceId,
				year: currentMonth.getFullYear(),
				month: currentMonth.getMonth() + 1,
				resources: selectedResourceIds,
				resourceMode,
				excludeBookingId: excludeBookingId || undefined,
			},
			{
				enabled: currentServiceId > 0,
			}
		);

	const monthData = monthAvailabilityData?.days ?? {};
	const loading = startingMonthLoading || monthLoading;

	// Load locale dynamically based on WordPress settings
	const [locale, setLocale] = useState<Locale | null>(null);
	const [localeLoading, setLocaleLoading] = useState(true);

	useEffect(() => {
		setLocaleLoading(true);
		getDayPickerLocale()
			.then((loadedLocale) => {
				setLocale(loadedLocale);
				setLocaleLoading(false);
			})
			.catch((error) => {
				// eslint-disable-next-line no-console
				console.error('Failed to load locale:', error);
				setLocale(getDefaultLocale());
				setLocaleLoading(false);
			});
	}, []);

	// Show skeleton while locale is loading
	if (localeLoading || !locale) {
		return (
			<div className="gpb-booking-time-picker__calendar">
				<CalendarSkeleton />
			</div>
		);
	}

	/**
	 * Get the week start day for the calendar.
	 *
	 * Defaults to WordPress setting from Settings » General.
	 * Can be filtered using Gravity Forms' applyFilters.
	 *
	 * @param {number} weekStartsOn    - Day of week (0 = Sunday, 1 = Monday, etc.)
	 * @param {Object} context         - Context object containing service and field information
	 * @param {Object} context.service - The current service object
	 *
	 * @example Force calendar to always start on Monday
	 * <code>
	 * window.gform.addFilter('gp_bookings_calendar_week_starts_on', function(weekStartsOn, context) {
	 *     return 1; // Monday
	 * });
	 * </code>
	 *
	 * @example Start on Monday for specific service
	 * <code>
	 * window.gform.addFilter('gp_bookings_calendar_week_starts_on', function(weekStartsOn, context) {
	 *     if (context.service?.id === 123) {
	 *         return 1; // Monday
	 *     }
	 *     return weekStartsOn;
	 * });
	 * </code>
	 */
	const weekStartsOn = window.gform?.applyFilters
		? window.gform.applyFilters(
				'gp_bookings_calendar_week_starts_on',
				getSettings().l10n?.startOfWeek ?? 0,
				{ service }
			)
		: (getSettings().l10n?.startOfWeek ?? 0);

	const handleRangeSelect = (range: DateRange | undefined) => {
		setSelectedRange(range);
	};

	// Custom handler for fixed-range selection (when min === max)
	const handleFixedRangeClick: DayEventHandler<React.MouseEvent> = (
		day,
		modifiers
	) => {
		if (modifiers.selected) {
			// Clear selection if clicking on already selected range
			setSelectedRange(undefined);
			return;
		}

		// Find a contiguous block of available dates that includes the clicked day
		const range = findAvailableRange(day, minDays);

		if (range) {
			setSelectedRange(range);
		}
	};

	// Find a contiguous block of available dates around the clicked day
	const findAvailableRange = (
		clickedDay: Date,
		requiredDays: number
	): DateRange | null => {
		// Helper to check if a date is available
		const isDateAvailable = (date: Date): boolean => {
			const dateKey = format(date, 'yyyy-MM-dd');
			const dayData = monthData[dateKey];

			// If no data, assume unavailable
			if (!dayData) {
				return false;
			}

			// Check if date is in the past
			if (date < today) {
				return false;
			}

			return dayData.available === true;
		};

		// First, try to find a range starting from the clicked day
		let startDate = clickedDay;
		let endDate = addDays(startDate, requiredDays - 1);

		// Check if all days from clicked day forward are available
		let allAvailable = true;
		for (let i = 0; i < requiredDays; i++) {
			const checkDate = addDays(startDate, i);
			if (!isDateAvailable(checkDate)) {
				allAvailable = false;
				break;
			}
		}

		if (allAvailable) {
			return { from: startDate, to: endDate };
		}

		// If forward doesn't work, try to find a contiguous block that includes the clicked day
		// by searching backwards and forwards to find the best fit

		// Find the start of the available block containing clicked day
		let blockStart = clickedDay;
		while (isDateAvailable(addDays(blockStart, -1))) {
			blockStart = addDays(blockStart, -1);
		}

		// Find the end of the available block containing clicked day
		let blockEnd = clickedDay;
		while (isDateAvailable(addDays(blockEnd, 1))) {
			blockEnd = addDays(blockEnd, 1);
		}

		// Calculate how many days are in this contiguous block
		const blockDays =
			Math.floor(
				(blockEnd.getTime() - blockStart.getTime()) /
					(1000 * 60 * 60 * 24)
			) + 1;

		// If the block is too small, return null
		if (blockDays < requiredDays) {
			return null;
		}

		// Try to center the range around the clicked day within the available block
		const daysBeforeClick = Math.floor(
			(clickedDay.getTime() - blockStart.getTime()) /
				(1000 * 60 * 60 * 24)
		);
		const daysAfterClick = Math.floor(
			(blockEnd.getTime() - clickedDay.getTime()) / (1000 * 60 * 60 * 24)
		);

		// Calculate ideal start position (trying to center around clicked day)
		const idealDaysBefore = Math.floor((requiredDays - 1) / 2);
		const idealDaysAfter = requiredDays - 1 - idealDaysBefore;

		if (
			daysBeforeClick >= idealDaysBefore &&
			daysAfterClick >= idealDaysAfter
		) {
			// Can center around clicked day
			startDate = addDays(clickedDay, -idealDaysBefore);
			endDate = addDays(clickedDay, idealDaysAfter);
		} else if (daysBeforeClick < idealDaysBefore) {
			// Not enough days before, align to start of block
			startDate = blockStart;
			endDate = addDays(blockStart, requiredDays - 1);
		} else {
			// Not enough days after, align to end of block
			endDate = blockEnd;
			startDate = addDays(blockEnd, -(requiredDays - 1));
		}

		return { from: startDate, to: endDate };
	};

	const commonProps: DayPickerProps = {
		month: currentMonth,
		captionLayout: 'dropdown' as const,
		onMonthChange,
		today,
		toYear: today.getFullYear() + 2,
		weekStartsOn,
		locale,
		disabled: [
			{ before: today },
			(date: Date) => {
				const dateKey = format(date, 'yyyy-MM-dd');
				const dayData = monthData[dateKey];

				// If no month data is available (e.g., during loading), don't disable
				// This prevents clearing selections when month data is being fetched
				if (!dayData) {
					return false;
				}

				// Prevent disabling a valid selected start/end date due to rules below.
				if (
					selectedRange?.from?.getTime() === date.getTime() ||
					selectedRange?.to?.getTime() === date.getTime()
				) {
					return false;
				}

				if (isDateRange) {
					// Utilize allowedStart/allowedEnd for more complicated rules like nightly.
					if (
						!selectedRange?.from &&
						typeof dayData?.allowedStart !== 'undefined'
					) {
						return !dayData?.allowedStart;
					}

					if (
						selectedRange?.from &&
						typeof dayData?.allowedStart !== 'undefined' &&
						typeof dayData?.allowedEnd !== 'undefined'
					) {
						const currentDate = date.getTime();
						const startDate = selectedRange.from.getTime();

						if (currentDate < startDate) {
							// For dates before start: check allowedStart (can be a new start date?)
							return !dayData?.allowedStart;
						} else if (currentDate > startDate) {
							// For dates after start: check allowedEnd (can be an end date?)
							return !dayData?.allowedEnd;
						}
						// For the start date itself, don't disable it (it's already selected)
					}
				}

				return !dayData?.available;
			},
		],
		components: {
			Month: (
				props: {
					calendarMonth: CalendarMonth;
					displayIndex: number;
				} & HTMLAttributes<HTMLDivElement>
			) => {
				const { calendarMonth, displayIndex, className, ...divProps } =
					props;

				return (
					<div
						{...divProps}
						className={classnames(
							className,
							loading ? 'loading' : ''
						)}
						{...(loading ? { inert: 'inert' } : {})}
					>
						{loading && (
							<div className="gpb-booking-time-picker__calendar-loading">
								<div className="gpb-booking-time-picker__calendar-loading-text">
									{__(
										'Loading available dates…',
										'gp-bookings'
									)}
								</div>
							</div>
						)}

						{props.children}
					</div>
				);
			},
		},
	};

	const DAY_IN_MINUTES = 1440;

	let minDays = 1;
	let maxDays: number | undefined = 1;
	let mode: Mode = 'single';

	if (service && service.blockSize >= DAY_IN_MINUTES) {
		// Calculate min/max days based on service settings
		// If minDuration is set, use that. Otherwise, use blockSize as minimum.
		minDays = Math.ceil(
			service.minDuration
				? service.minDuration / DAY_IN_MINUTES
				: service.blockSize / DAY_IN_MINUTES
		);

		// If maxDuration is set, use that. Otherwise, if flexibleDuration is false, use minDays as maximum.
		maxDays = service.maxDuration
			? Math.ceil(service.maxDuration / DAY_IN_MINUTES)
			: service.flexibleDuration
				? undefined
				: minDays;

		// Enable range selection if flexibleDuration is true
		if (service.flexibleDuration) {
			mode = 'range';
		}
	}

	// Detect fixed-range mode (when min === max)
	const isFixedRange = minDays === maxDays && minDays > 1;

	return (
		<div className="gpb-booking-time-picker__calendar">
			{mode === 'range' ? (
				isFixedRange ? (
					// Use custom selection for fixed-range mode (min === max)
					<DayPicker
						onDayClick={handleFixedRangeClick}
						modifiers={{
							selected: selectedRange,
							range_start: selectedRange?.from,
							range_end: selectedRange?.to,
							range_middle: (date: Date) => {
								if (
									!selectedRange?.from ||
									!selectedRange?.to
								) {
									return false;
								}
								// Check if date is between start and end (excluding ends)
								return (
									date > selectedRange.from &&
									date < selectedRange.to
								);
							},
						}}
						{...commonProps}
					/>
				) : (
					// Use built-in range mode for flexible ranges (min < max)
					<DayPicker
						mode="range"
						selected={selectedRange}
						onSelect={handleRangeSelect}
						excludeDisabled={true} // Prevent selection of disabled days in range mode
						min={minDays}
						max={maxDays}
						{...commonProps}
					/>
				)
			) : (
				<DayPicker
					mode="single"
					selected={selectedDate}
					onSelect={setSelectedDate}
					{...commonProps}
				/>
			)}
			{mode === 'range' && selectedRange?.from && selectedRange?.to && (
				<div className="gpb-booking-time-picker__range-info">
					<p>
						{service?.treatAsNights ? (
							<>
								{__('Check-in:', 'gp-bookings')}{' '}
								{formatFullDate(selectedRange.from)}
								<br />
								{__('Check-out:', 'gp-bookings')}{' '}
								{formatFullDate(selectedRange.to)}
								<br />
								{__('Total nights:', 'gp-bookings')}{' '}
								{Math.floor(
									(selectedRange.to.getTime() -
										selectedRange.from.getTime()) /
										(1000 * 60 * 60 * 24)
								)}
							</>
						) : (
							<>
								{__('From:', 'gp-bookings')}{' '}
								{formatFullDate(selectedRange.from)}
								<br />
								{__('To:', 'gp-bookings')}{' '}
								{formatFullDate(selectedRange.to)}
								<br />
								{__('Total days:', 'gp-bookings')}{' '}
								{Math.floor(
									(selectedRange.to.getTime() -
										selectedRange.from.getTime()) /
										(1000 * 60 * 60 * 24)
								) + 1}
							</>
						)}
					</p>
				</div>
			)}
		</div>
	);
}
